import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { getAccessToken, logout } from '../utils/auth';
import { listReceivedShares, acceptShare, declineShare } from '../utils/apiContentSharing';
import { asArray, shareSharer, shareTitle, formatShareDate } from '../utils/sharesFields';

const isPending = (share) => (share.status || '').toLowerCase() === 'pending';

function SharedWithMePanel() {
  const [shares, setShares] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      if (!getAccessToken()) { logout(); return; }
      // status=all so invitations show up next to the projects they will become.
      setShares(asArray(await listReceivedShares(false, 'all')));
    } catch (err) {
      if (err.response?.status === 401) { logout(); return; }
      setError(err.request ? 'Network error: Could not load shared content.' : 'Could not load shared content.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const respond = async (shareId, decision) => {
    setBusyId(shareId);
    setError('');
    try {
      await (decision === 'accept' ? acceptShare(shareId) : declineShare(shareId));
      await load();
    } catch (err) {
      if (err.response?.status === 401) { logout(); return; }
      // 410 means the invitation was already settled elsewhere — reload to show
      // the truth, then report, since load() clears the banner.
      const message = err.response?.status === 410
        ? 'That invitation is no longer pending.'
        : `Could not ${decision} the invitation. Please try again.`;
      await load();
      setError(message);
    } finally {
      setBusyId(null);
    }
  };

  const banner = error ? (
    <div className="mt-8 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
      <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
    </div>
  ) : null;

  if (loading && shares.length === 0) return null;
  if (shares.length === 0) return banner;

  return (
    <div className="mt-8">
      <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Shared with me</h2>
      {banner}
      <div className="bg-white dark:bg-background-dark/50 border border-gray-200 dark:border-gray-800 rounded-lg shadow overflow-hidden divide-y divide-gray-200 dark:divide-gray-700">
        {shares.map((share) => {
          const projectId = share.project_id || share.id;
          const pending = isPending(share);
          const busy = busyId === share.share_id;
          return (
            <div
              key={share.share_id || projectId}
              className="flex items-center justify-between gap-4 px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-800/30"
            >
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium text-gray-900 dark:text-white truncate">
                  {/* A pending invitation is not openable until it is accepted. */}
                  {pending ? shareTitle(share) : (
                    <Link to={`/projects/${projectId}/chapters`} className="hover:text-primary">
                      {shareTitle(share)}
                    </Link>
                  )}
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  {pending ? 'Invitation from' : 'Shared by'} {shareSharer(share)}
                  {share.shared_at && <> · {formatShareDate(share.shared_at)}</>}
                </div>
              </div>
              {pending && (
                <div className="shrink-0 flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => respond(share.share_id, 'accept')}
                    disabled={busy}
                    className="text-xs px-2.5 py-1 bg-primary text-white rounded hover:bg-primary/90 transition-colors disabled:opacity-50"
                  >
                    {busy ? 'Working…' : 'Accept'}
                  </button>
                  <button
                    type="button"
                    onClick={() => respond(share.share_id, 'decline')}
                    disabled={busy}
                    className="text-xs px-2.5 py-1 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors disabled:opacity-50"
                  >
                    Decline
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default SharedWithMePanel;
